import { useState } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

const IMAGE_URL_PATTERN = /https?:\/\/[^\s)]+\.(?:jpe?g|png|webp|gif)(?:\?[^\s)]*)?/gi

interface AddOnNoteMediaProps {
  note: string
  label?: string | null
  className?: string
  thumbnailClassName?: string
}

export function AddOnNoteMedia({
  note,
  label,
  className,
  thumbnailClassName,
}: AddOnNoteMediaProps) {
  const imageUrls = getImageUrlsFromNote(note)
  const text = imageUrls.reduce((current, url) => current.replace(url, ""), note).trim()
  const [openImage, setOpenImage] = useState<string | null>(null)

  if (imageUrls.length === 0) {
    return <span className={className}>{note}</span>
  }

  return (
    <span className={cn("inline-flex flex-wrap items-center gap-x-2 gap-y-1", className)}>
      {text && <span>{text}</span>}

      <span className="inline-flex flex-wrap gap-2">
        {imageUrls.map((url, index) => (
          <Button
            key={`${url}-${index}`}
            type="button"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation()
              setOpenImage(url)
            }}
            className={cn(
              "h-12 w-16 overflow-hidden rounded-lg border border-border bg-muted/40 p-0 hover:border-primary/50",
              thumbnailClassName
            )}
            aria-label={`Open ${label ?? "add-on"} image ${index + 1}`}
          >
            <img
              src={url}
              alt={`${label ?? "Add-on"} image ${index + 1}`}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </Button>
        ))}
      </span>

      <Dialog open={!!openImage} onOpenChange={(open) => !open && setOpenImage(null)}>
        <DialogContent
          className="fixed inset-0 z-50 h-screen w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0 bg-black/90 p-0 @min-[40rem]:inset-0 @min-[40rem]:h-screen @min-[40rem]:w-screen @min-[40rem]:max-w-none @min-[40rem]:translate-x-0 @min-[40rem]:translate-y-0 @min-[40rem]:rounded-none @min-[40rem]:p-0"
          showCloseButton={false}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setOpenImage(null)}
            className="absolute top-3 right-3 z-10 size-10 rounded-full bg-black/40 text-white/70 backdrop-blur-sm hover:bg-black/60 hover:text-white"
            aria-label="Close image gallery"
          >
            <X className="size-5" />
          </Button>

          <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 p-4">
            <img
              src={openImage ?? ""}
              alt={`${label ?? "Add-on"} image`}
              className="min-h-0 max-h-[calc(100vh-7rem)] max-w-full flex-1 object-contain"
            />

            {imageUrls.length > 1 && (
              <div className="flex max-w-full shrink-0 justify-center gap-2 overflow-x-auto pb-1">
                {imageUrls.map((url, index) => (
                  <Button
                    key={`${url}-gallery-${index}`}
                    type="button"
                    variant="ghost"
                    onClick={() => setOpenImage(url)}
                    className={cn(
                      "h-auto overflow-hidden rounded-lg border p-0 transition-all",
                      url === openImage ? "border-primary" : "border-white/30"
                    )}
                    aria-label={`Show ${label ?? "add-on"} image ${index + 1}`}
                  >
                    <img
                      src={url}
                      alt={`${label ?? "Add-on"} thumbnail ${index + 1}`}
                      className="h-16 w-24 object-cover"
                    />
                  </Button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </span>
  )
}

function getImageUrlsFromNote(note: string) {
  return [...note.matchAll(IMAGE_URL_PATTERN)].map((match) => match[0])
}
